import camelCase from 'lodash/camelCase';
import { useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { PickYourMealsContext } from 'components/signUpFunnel/Contexts';
import { BrandContext } from 'context/BrandContext';
import {
  dashboardRootPath,
  getBrandRoute,
  PICK_MEALS_BILLING_PATH,
  PICK_MEALS_THANK_YOU_PATH,
} from 'helpers/routes';
import useFunnelMealPlan from 'hooks/useFunnelMealPlan';
import useFunnelNavigation from 'hooks/useFunnelNavigation';

import { UserContext } from './Contexts';

const isCompletedSignupRedirectRequired = ({ user, locationPathname, routes }) => {
  if (!user.completedSignup) {
    return false;
  }

  return (
    !locationPathname.includes(routes.thankyou) &&
    !locationPathname.includes(routes.pickMealsThankYou)
  );
};

const isUserRouteRedirectRequired = ({
  userRoute,
  locationPathname,
  currentStep,
  userStep,
  routes,
}) => {
  if (!userRoute || userRoute === locationPathname) {
    return false;
  }

  const isPastAllowedStep = !currentStep || Number(currentStep.order) > Number(userStep?.order);
  const isThankYouPath =
    locationPathname.includes(routes.thankyou) ||
    locationPathname.includes(routes.pickMealsThankYou);

  return isPastAllowedStep && !isThankYouPath;
};

const getPickYourMealsStatusRoute = ({ userStatus, routes }) => {
  const pickYourMealsRoutesByStatus = {
    onPickMealsStep: routes.pickMeals,
    onPickMealsBillingStep: routes.pickMealsBilling,
    onPickMealsThankYouStep: routes.pickMealsThankYou,
  };

  return pickYourMealsRoutesByStatus[camelCase(userStatus)] || null;
};

const FunnelUserWrapper = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { brand } = useContext(BrandContext);
  const { isPickYourMeals, isPickYourMealsSlideOut } = useContext(PickYourMealsContext);
  const { currentStep, userStep, userRoute } = useFunnelNavigation();
  const mealPlanSlug = useFunnelMealPlan();
  const isPickYourMealsBillingOrThankYouPath =
    location.pathname === PICK_MEALS_BILLING_PATH ||
    location.pathname === PICK_MEALS_THANK_YOU_PATH;
  const isPickYourMealsMode = isPickYourMeals || isPickYourMealsBillingOrThankYouPath;

  useEffect(() => {
    if (!user || !brand) {
      return;
    }

    const routes = getBrandRoute({ brand, addSuffix: false });

    if (
      isCompletedSignupRedirectRequired({
        user,
        locationPathname: location.pathname,
        routes,
      })
    ) {
      window.location.replace(dashboardRootPath());
      return;
    }

    if (isPickYourMealsMode) {
      if (isPickYourMealsSlideOut || isPickYourMealsBillingOrThankYouPath) {
        return;
      }

      const pickYourMealsStatusRoute = getPickYourMealsStatusRoute({
        userStatus: user.status,
        routes,
      });

      if (pickYourMealsStatusRoute && pickYourMealsStatusRoute !== location.pathname) {
        navigate(pickYourMealsStatusRoute, { replace: true });
      }

      return;
    }

    if (
      isUserRouteRedirectRequired({
        userRoute,
        locationPathname: location.pathname,
        currentStep,
        userStep,
        routes,
      })
    ) {
      const isFunnelStale = userRoute && !userRoute.includes(`/${mealPlanSlug}`);
      if (!isFunnelStale) {
        navigate(userRoute, { replace: true });
      }
    }
  }, [
    user,
    navigate,
    brand,
    location.pathname,
    isPickYourMealsMode,
    currentStep,
    userStep,
    userRoute,
    isPickYourMealsSlideOut,
    isPickYourMealsBillingOrThankYouPath,
    user?.status,
    mealPlanSlug,
  ]);

  return children;
};

export default FunnelUserWrapper;
